import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/templates/PageLayout';
import {
  OPERATOR_ADDRESS,
  OPERATOR_EMAIL,
  OPERATOR_NAME,
  PRIVACY_EFFECTIVE_DATE,
  PRIVACY_TITLE,
  PRIVACY_VERSION,
  MAILTO_HREF,
  appendixLead,
  appendixParagraphs,
  appendixTitle,
  coversList,
  introClose,
  introParagraphs,
  sections,
  shortVersionItems,
} from '../data/privacyPolicy';

const linkClass =
  'text-navy underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow';

const TOKEN_RE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

export const InlineText = ({ text }) => {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  const pattern = new RegExp(TOKEN_RE.source, 'g');

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else {
      const [, label, href] = token.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (href.startsWith('/')) {
        nodes.push(
          <Link key={key} to={href} className={linkClass}>
            {label}
          </Link>
        );
      } else {
        nodes.push(
          <a
            key={key}
            href={href}
            className={linkClass}
            {...(href.startsWith('http') ? { rel: 'noopener noreferrer' } : {})}
          >
            {label}
          </a>
        );
      }
    }
    key += 1;
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return <>{nodes}</>;
};

const OperatorBlock = ({ inline = false }) => {
  if (inline) {
    return (
      <p>
        {OPERATOR_NAME} · {OPERATOR_ADDRESS} ·{' '}
        <a href={MAILTO_HREF} className={linkClass}>
          {OPERATOR_EMAIL}
        </a>
      </p>
    );
  }

  return (
    <address className="not-italic mb-4">
      <p>{OPERATOR_NAME}</p>
      <p>{OPERATOR_ADDRESS}</p>
      <p>
        Email:{' '}
        <a href={MAILTO_HREF} className={linkClass}>
          {OPERATOR_EMAIL}
        </a>
      </p>
    </address>
  );
};

const PolicyTable = ({ headers, rows }) => (
  <div className="overflow-x-auto mb-6">
    <table className="w-full min-w-[36rem] text-left border border-gray-200 text-sm md:text-base">
      <thead className="bg-gray-50">
        <tr>
          {headers.map((header) => (
            <th key={header} scope="col" className="px-3 py-2 font-semibold text-navy border-b border-gray-200">
              <InlineText text={header} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.join('|')} className="align-top">
            {row.map((cell, index) => (
              <td key={`${row[0]}-${index}`} className="px-3 py-2 border-t border-gray-200 text-gray-800">
                <InlineText text={cell} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Block = ({ block }) => {
  if (block.type === 'p') {
    return (
      <p className="mb-4 text-gray-800">
        <InlineText text={block.text} />
      </p>
    );
  }
  if (block.type === 'h3') {
    return (
      <h3 id={block.id} className="text-xl font-bold text-navy mt-8 mb-3">
        <InlineText text={block.text} />
      </h3>
    );
  }
  if (block.type === 'ul') {
    return (
      <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
        {block.items.map((item) => (
          <li key={item}>
            <InlineText text={item} />
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === 'ol') {
    return (
      <ol className="list-decimal pl-6 mb-4 space-y-2 text-gray-800">
        {block.items.map((item) => (
          <li key={item}>
            <InlineText text={item} />
          </li>
        ))}
      </ol>
    );
  }
  if (block.type === 'table') {
    return <PolicyTable headers={block.headers} rows={block.rows} />;
  }
  if (block.type === 'operator') {
    return <OperatorBlock inline={block.inline} />;
  }
  return null;
};

const Privacy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | DataRook';
  }, []);

  return (
    <PageLayout>
      <article className="dr-container py-16 max-w-4xl">
        <h1 className="text-3xl md:text-4xl font-black text-navy mb-4">{PRIVACY_TITLE}</h1>
        <p className="text-gray-700 mb-8">
          <strong>Effective date:</strong>{' '}
          <time dateTime="2026-09-06">{PRIVACY_EFFECTIVE_DATE}</time>
          {'  '}
          <strong>Version:</strong> {PRIVACY_VERSION}
        </p>

        {introParagraphs.map((paragraph) => (
          <p key={paragraph} className="mb-4 text-gray-800">
            <InlineText text={paragraph} />
          </p>
        ))}
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          {coversList.map((item) => (
            <li key={item}>
              <InlineText text={item} />
            </li>
          ))}
        </ul>
        <p className="mb-10 text-gray-800">
          <InlineText text={introClose} />
        </p>

        <h2 className="text-2xl font-bold text-navy mb-4">The short version</h2>
        <ul className="list-disc pl-6 mb-12 space-y-3 text-gray-800">
          {shortVersionItems.map((item) => (
            <li key={item}>
              <InlineText text={item} />
            </li>
          ))}
        </ul>

        {sections.map((section) => (
          <section key={section.id} aria-labelledby={section.id} className="mb-12">
            <h2 id={section.id} className="text-2xl font-bold text-navy mb-4 scroll-mt-24">
              {section.number}. {section.title}
            </h2>
            {section.blocks.map((block, index) => (
              <Block key={`${section.id}-${index}`} block={block} />
            ))}
          </section>
        ))}

        <section aria-labelledby="appendix" className="mb-8 border-t border-gray-200 pt-10">
          <h2 id="appendix" className="text-2xl font-bold text-navy mb-4 scroll-mt-24">
            {appendixTitle}
          </h2>
          <p className="font-semibold text-navy mb-4">{appendixLead}</p>
          {appendixParagraphs.map((paragraph) => (
            <p key={paragraph} className="mb-4 text-gray-800">
              <InlineText text={paragraph} />
            </p>
          ))}
        </section>
      </article>
    </PageLayout>
  );
};

export default Privacy;
